/**
 * iOS audio-session guards.
 *
 * Two WebKit defects make a page silent while every API reports success. Both
 * are engine bugs, so the page cannot detect them — it can only make the
 * expected state explicit and re-assert it at the moments the engine forgets.
 *
 * 1. `navigator.audioSession.type` defaults to `auto`, which on iOS resolves to
 *    an ambient session that the physical Ring/Silent switch hard-mutes.
 *    Campaign music is playback audio, so the type must be `playback`
 *    (WebKit bug 323104, and the older mute-switch reports before it).
 *
 * 2. After a GPU-process relaunch the web process's cached session category is
 *    stale, so the new session falls back to ambient and renders into silence.
 *    Re-asserting `playback` is a no-op because the engine short-circuits an
 *    unchanged value; cycling through a different type forces the update
 *    through. That is the workaround recorded in the same bug.
 *
 * Both are no-ops on browsers without `navigator.audioSession` (all of them
 * except recent iOS Safari, including Chrome-on-iOS which shares the engine
 * but may not expose the object).
 */

/** The session type campaign music wants: audible with the Ring/Silent switch. */
export const PLAYBACK_SESSION = "playback"

/** The throwaway type used to defeat the engine's "unchanged value" cache. */
export const CYCLE_SESSION = "ambient"

export function audioSessionOf(nav) {
  return nav?.audioSession || null
}

/** True when the session is not already what playback audio needs. */
export function sessionNeedsPlayback(session) {
  if (!session) return false
  return session.type !== PLAYBACK_SESSION
}

/**
 * Assert `playback` without touching the engine when it is already set.
 * Returns true when a write happened, so callers can log/report it once.
 */
export function assertPlayback(session) {
  if (!sessionNeedsPlayback(session)) return false
  try {
    session.type = PLAYBACK_SESSION
    return true
  } catch {
    return false
  }
}

/**
 * Force a real category change through a stale cache: set a different type,
 * then the wanted one on the next task. Returns true when the cycle was
 * scheduled.
 *
 * `schedule` is injectable so the rule is testable without a timer.
 */
export function cyclePlaybackSession(session, schedule = (fn) => setTimeout(fn, 0)) {
  if (!session) return false
  try {
    session.type = CYCLE_SESSION
    schedule(() => {
      try {
        session.type = PLAYBACK_SESSION
      } catch {
        /* ignore */
      }
    })
    return true
  } catch {
    return false
  }
}

/**
 * The recovery call for bug 323104, from a user gesture: a stale category is
 * only cleared by an actual change, so go through a different value and back to
 * `playback` on the next task.
 */
export function recoverPlaybackSession(session, schedule = (fn) => setTimeout(fn, 0)) {
  if (!session) return false
  try {
    session.type = CYCLE_SESSION
    schedule(() => {
      try {
        session.type = PLAYBACK_SESSION
      } catch {
        /* ignore */
      }
    })
    return true
  } catch {
    return false
  }
}

export default {
  PLAYBACK_SESSION,
  CYCLE_SESSION,
  audioSessionOf,
  sessionNeedsPlayback,
  assertPlayback,
  cyclePlaybackSession,
  recoverPlaybackSession,
}
